// init-app-desktop.js — 桌面端专用初始化（initApp + destroyApp + switchFeature）
// 按 Phase 1-5 流程编排，首屏关键路径仅包含 DOM 绑定 + auth + 一次数据渲染

import { ensureLogin } from '@shared-gate/ensure-login.js';
import { get as getSessionCache } from '../lib/session-cache.js';
import { initSettingsFromCache } from './settings-modal.js';
import { initFolderModal } from './folder-modal.js';
import { initDropdown } from './dropdown.js';
import { initArrowDelegation, initHoverPrefetch } from './sidebar-tree.js';
import { initSidebarDrag, destroySidebarDrag } from './sidebar-drag.js';
import { initFileSearchEvents, destroyFileSearchEvents, updateSearchPlaceholder, _clearSearchInputs } from './file-search.js';
import { initCollapsibleSearch } from './collapsible-search.js';
import { initViewNavDesktop } from './view-nav-desktop.js';
import { initSidebarToolbar } from './workspace-toolbar.js';
import { initOpenLocalFile, destroyOpenLocalFile } from './open-local-file.js';
import { InspirationPush } from './inspiration-push.js';
import { MusicPlayer } from './music-player.js';
import { initToolkit } from './toolkit.js';
import { apiFetch } from './auth-api.js';
import { getFeature } from './feature-loader.js';
import { showUserInfo, showLoginButton } from './user-info.js';
import { initAppEntry, registerGlobalEvents, cleanupGlobalEvents } from './init-shared.js';
import { showToast } from './file-utils.js';
import { setFolderPanelVisible } from './file-loader.js';

// 桌面端全局状态
var _sidebarPlayerObserver = null;
var _chatNavListenersInited = false;
var _mountedFeatures = []; // 已 mount 的 Feature 对象引用（destroy 时遍历清理）
var _shellInited = false; // Shell 一次性初始化守卫：跨 chunk 整页销毁时重置，同 chunk 切页保持常驻

// 播放器辅助：控制 #globalPlayer 显隐（桌面端版本，仅 visibility 控制）
function _remountPlayerToWorkspace() {
  try {
    if (!MusicPlayer.isInited() || !MusicPlayer.getCurrentRecordId()) return;
    var container = document.getElementById('globalPlayer');
    if (!container) return;
    container.style.display = '';
  } catch (e) {}
}

// 设置 #globalPlayer 定位类 + 初始隐藏
function _setupGlobalPlayerClass() {
  var gp = document.getElementById('globalPlayer');
  if (!gp) return;
  gp.className = '';
  gp.classList.add('global-player--workspace-desktop');
  gp.style.display = 'none';
}

// action-bar 显隐控制
function _setActionBarVisible(visible) {
  var bar = document.querySelector('.action-bar');
  if (bar) bar.style.display = visible ? '' : 'none';
}

// 桌面端 syncNavHighlight：操作 .sidebar__btn、#navTree、action-bar
// feature 视图 / 默认收藏视图统一隐藏文件夹树面板（§3.3 面板显隐由视图状态驱动）
function _syncNavHighlightDesktop(feature) {
  document.querySelectorAll('.sidebar__btn.active').forEach(function(b) { b.classList.remove('active'); });
  document.querySelectorAll('#navTree .folder-item.active').forEach(function(el) { el.classList.remove('active'); });
  var topChatBtn = document.querySelector('.top-header-chat-btn[data-action="chat"]');
  if (topChatBtn) topChatBtn.classList.remove('active');

  if (feature) {
    _setActionBarVisible(false);
    if (feature === 'chat') {
      if (topChatBtn) topChatBtn.classList.add('active');
    } else {
      var btn = document.querySelector('.sidebar__btn[data-action="' + feature + '"]');
      if (btn) btn.classList.add('active');
    }
  } else {
    // 默认视图（收藏）：高亮"收藏"按钮 + 不隐藏 action-bar
    var starBtn = document.querySelector('.sidebar__btn[data-action="star"]');
    if (starBtn) starBtn.classList.add('active');
    _setActionBarVisible(true);
  }

  // 工具栏按钮触发的 feature / 默认收藏视图均不显示文件夹树面板
  setFolderPanelVisible(false);
}

async function _renderFeatureViewDesktop(feature) {
  // 先变 UI（同步）：隐藏操作栏、清搜索、高亮侧边栏、设搜索占位 —— 再异步载数据（getFeature + mount）
  _setActionBarVisible(false);
  _clearSearchInputs();
  _syncNavHighlightDesktop(feature);
  updateSearchPlaceholder(feature === 'inspiration' ? 'notes' : feature);

  if (feature === 'diary') {
    var diaryMod = await getFeature('diary');
    _mountedFeatures.push(diaryMod.DiaryFeature);
    await diaryMod.DiaryFeature.mount(null, { mode: 'full' });
  } else if (feature === 'todo') {
    var todoMod = await getFeature('todo');
    _mountedFeatures.push(todoMod.TodoFeature);
    await todoMod.TodoFeature.mount(null, { mode: 'full' });
  } else if (feature === 'inspiration') {
    var inspMod = await getFeature('inspiration');
    _mountedFeatures.push(inspMod.InspirationFeature);
    await inspMod.InspirationFeature.mount(null, { mode: 'full' });
  } else if (feature === 'music') {
    var musicMod = await getFeature('music');
    _mountedFeatures.push(musicMod.MusicFeature);
    await musicMod.MusicFeature.mount(null, { mode: 'full' });
  } else if (feature === 'bill') {
    // 记账视图（v1.0.046：第五个 Feature，桌面排布）
    var billMod = await getFeature('bill');
    _mountedFeatures.push(billMod.BillFeature);
    await billMod.BillFeature.mount(null, { mode: 'full' });
  } else if (feature === 'project') {
    // 项目视图（第六个 Feature，桌面排布）
    var projMod = await getFeature('project');
    _mountedFeatures.push(projMod.ProjectFeature);
    await projMod.ProjectFeature.mount(null, { mode: 'full' });
  } else if (feature === 'home') {
    // 首页工作台（零依赖首屏：mount 同步渲染；搜索框隐藏由 HomeFeature.mount 内聚处理）
    var homeMod = await getFeature('home');
    _mountedFeatures.push(homeMod.HomeFeature);
    await homeMod.HomeFeature.mount(null, { mode: 'full' });
  }
}

// 挂载文件内核 Feature（收藏/飞书/云盘/回收站统一入口），并登记到 _mountedFeatures 供 destroy 清理
async function _mountFileBrowser(view, source) {
  var fbMod = await getFeature('fileBrowser');
  _mountedFeatures.push(fbMod.FileBrowserFeature);
  await fbMod.FileBrowserFeature.mount(null, { view: view, source: source });
}

// ===== 公开 API =====

export async function initAppDesktop(feature, isFeatureSwitch) {
  // Phase 1: Bootstrap（串行）
  await ensureLogin();
  var cache = getSessionCache();
  initSettingsFromCache(cache ? cache.settings : null);

  // ===== Shell 一次性初始化（同 chunk 切页不重跑；跨 chunk 整页销毁时 _shellInited 重置后重新执行）=====
  if (!_shellInited) {
    _shellInited = true;

    // Phase 2: DOM Infrastructure（同步，按依赖顺序）
    initFolderModal();
    initDropdown();
    initArrowDelegation();
    initHoverPrefetch();
    initSidebarDrag();
    initFileSearchEvents();
    // 可折叠搜索按钮（桌面端，方向由 CSS 类 --right 决定）：结构在 HTML 模板里，这里只绑展开/收起行为（幂等）
    var searchCollapse = document.querySelector('.top-header .collapsible-search');
    if (searchCollapse) initCollapsibleSearch(searchCollapse);
  initViewNavDesktop(switchFeatureDesktop);
  initSidebarToolbar(switchFeatureDesktop, switchFileBrowserDesktop);
    initOpenLocalFile();
    InspirationPush.ensurePlaceholder();

    // 播放器高度联动主内容区底部留白（播放条通栏显示时最后一行不被遮挡）
    try {
      var gp = document.getElementById('globalPlayer');
      var mainContent = document.querySelector('.main-content');
      if (gp && mainContent && window.matchMedia('(min-width: 1025px)').matches) {
        var updateMainPad = function() {
          var playerH = gp.style.display === 'none' ? 0 : gp.offsetHeight;
          mainContent.style.paddingBottom = (playerH > 0 ? playerH : 0) + 'px';
        };
        updateMainPad();
        _sidebarPlayerObserver = new ResizeObserver(function() { updateMainPad(); });
        _sidebarPlayerObserver.observe(gp);
      }
    } catch (e) {}

    // Phase 2.5: 共享事件绑定（登录/上传/用户菜单/图标，其中 initAppEntry 内部已做 data-icon 图标替换）
    initAppEntry();

    // 小雀聊天面板事件监听（横幅点击 + 卡片导航）
    if (!_chatNavListenersInited) {
      _chatNavListenersInited = true;
      document.addEventListener('xiaque:open-chat-from-banner', function() {
        import('./desktop-chat-panel.js').then(function(mod) {
          mod.openDesktopChatPanel();
        }).catch(function() {});
      });
      document.addEventListener('xiaque:navigate', function(e) {
        var target = e.detail && e.detail.target;
        if (target === 'todo') {
          import('./desktop-chat-panel.js').then(function(mod) {
            if (mod.isDesktopChatPanelOpen && mod.isDesktopChatPanelOpen()) {
              mod.closeDesktopChatPanel();
            }
          }).catch(function() {});
          history.replaceState(null, '', '/todo');
          switchFeatureDesktop('todo');
        }
      });
    }

    // 小雀 Agent 后台预加载（未读角标 + 轮询）
    var chatBadge = document.getElementById('desktopChatBadge');
    if (chatBadge) {
      import('./secretary-core.js').then(function(mod) {
        mod.setUnreadBadgeElement(chatBadge);
        mod.initSecretaryAgent();
      }).catch(function() {});
    }

    // Phase 5 第 1 批：全局播放器 + 全局事件（Shell 一次性，跨 feature 切换不重跑）
    var onMusicState = function(e) {
      if (e.detail && e.detail.recordId) _remountPlayerToWorkspace();
    };
    requestIdleCallback(function() {
      initToolkit();
      MusicPlayer.init(null, { context: 'workspace' });
      _setupGlobalPlayerClass();
      _remountPlayerToWorkspace();
      registerGlobalEvents(onMusicState);
    }, { timeout: 2000 });
  }

  // Phase 3: Auth & Sidebar
  var urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('error') === 'access_denied' || urlParams.get('error') === 'oauth_denied') {
    showToast('授权已取消。如需使用，请重新登录。');
  } else if (urlParams.get('error') === 'oauth_failed') {
    showToast('授权失败，请重试');
  }

  // 头像/登录态判断依据从「飞书 OAuth」改为「门禁」（门禁登录即可正常显示头像，不要求连飞书）
  // 门禁状态已由 ensureLogin 写入 session 缓存 cache.gate
  var gate = (cache && cache.gate) || {};
  if (!gate.logged_in) {
    showLoginButton();
    return;
  }

  // 构造门禁用户对象（头像三级规则数据源：昵称 / 用户名 / 项目头像 URL）
  var avatar = (cache && cache.avatar) || {};
  var gateUser = {
    username: gate.username || '',
    nickname: ((cache && cache.settings && cache.settings.profileNickname) || ''),
    avatar_url: avatar.avatar_url || '',
    has_avatar: avatar.has_avatar || false
  };
  showUserInfo(gateUser);

  // 应用启动不再自动渲染文件树（飞书连接判断优化：不预设默认源、不自动加载文件列表/树，
  // 进哪个文件列表由用户点「云盘/飞书」决定）。文件树的渲染现仅在 loadFiles 内部触发。

  // Phase 4: First-Screen Data Rendering
  if (feature) {
    await _renderFeatureViewDesktop(feature);
  } else {
    // 默认视图：收藏（经 fileBrowser Feature 统一挂载）
    await _mountFileBrowser('star', null);
    _syncNavHighlightDesktop(feature);
  }

  // Phase 5: Post-Render（requestIdleCallback 分批；第 1 批已并入 Shell 一次性初始化）

  // 🟡 第 2 批
  requestIdleCallback(function() {
    if (!isFeatureSwitch) InspirationPush.init();
  }, { timeout: 4000 });

  // 🔵 第 3 批
  // fileBrowser：默认视图换首页后补偿预载（点击"文档/收藏"依然近秒开）；
  // home：从 /list 等非首页入口进入时，二次切首页零等待
  requestIdleCallback(function() {
    import('@modules/desktop-chat-panel.js').catch(function() {});
    getFeature('fileBrowser').catch(function() {});
    getFeature('diary').catch(function() {});
    getFeature('todo').catch(function() {});
    getFeature('inspiration').catch(function() {});
    getFeature('music').catch(function() {});
    getFeature('bill').catch(function() {});
    getFeature('project').catch(function() {});
    getFeature('home').catch(function() {});
  }, { timeout: 10000 });
}

export function destroyAppDesktop(isFeatureSwitch) {
  // Shell 级 teardown：跨 chunk 整页销毁时执行（DOM 将被 replaceChildren 重建）；
  // 同 chunk 切页跳过，Shell 保持常驻，避免反复解绑/重绑监听（如 sidebar-drag 的 divider mousedown 会泄漏堆积）
  if (!isFeatureSwitch) {
    _shellInited = false; // 重置 Shell 一次性守卫：跨 chunk 后新 DOM 需重新做 Shell 初始化
    if (_sidebarPlayerObserver) { _sidebarPlayerObserver.disconnect(); _sidebarPlayerObserver = null; }
    try { destroySidebarDrag(); } catch (e) {}
    try { destroyOpenLocalFile(); } catch (e) {}
    cleanupGlobalEvents();
    var gp = document.getElementById('globalPlayer');
    if (gp) { gp.classList.remove('global-player--workspace-desktop', 'global-player--workspace-mobile'); }
    var mainC = document.querySelector('.main-content');
    if (mainC) mainC.style.paddingBottom = '';
  }

  // 视图级清理（每次切页都做）
  try { destroyFileSearchEvents(); } catch (e) {} // 清全局搜索 debounce 计时器，防跨视图残留搜索
  document.body.classList.remove('cloud-panel-open');
  // 遍历已挂载 Feature 调 destroy（内聚清理：解绑监听/清模块状态/清 Shell 插槽，替代手写补丁段）
  var mounted = _mountedFeatures.splice(0);
  mounted.forEach(function(f) {
    try { if (f && f.destroy) f.destroy(); } catch (e) {}
  });
  // InspirationPush 不在此销毁：推送条在子页面切换时保持不动，仅在 chunk 更换时随 DOM 一起被替换
  // 兜底清理：桌面端聊天浮窗残留 DOM（正常路径由 closeDesktopChatPanel 清理）
  try {
    var panel = document.querySelector('.xiaque-desktop-panel');
    if (panel) panel.remove();
  } catch (e) {}
}

export async function switchFeatureDesktop(feature) {
  destroyAppDesktop(true);
  await initAppDesktop(feature, true);
}

// 文件视图切换入口：文件源间（收藏↔飞书↔云盘↔回收站）轻量切换走 refresh，
// 避免 destroy 里 remove 'cloud-panel-open' 造成文件夹面板/播放器宽度两次闪烁；跨大类（应用型→文件）才 destroy→mount
export async function switchFileBrowserDesktop(view, source) {
  var isFileView = _mountedFeatures.some(function(f) { return f && f.name === 'fileBrowser'; });
  if (isFileView) {
    var fbMod = await getFeature('fileBrowser');
    await fbMod.FileBrowserFeature.refresh({ view: view, source: source });
  } else {
    destroyAppDesktop(true);
    await _mountFileBrowser(view, source);
  }
}
